// deps
import { useContext, useCallback, useMemo } from "react";

// internal-deps
import { SavingsLineItemData, SavingsRightSectionType } from "@swiggy-private/cart-types";
import { format, isEmpty } from "@swiggy-private/js-utils";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers
import { getIMCouponlessSavingWidgetData } from "@cart-client/helpers/GiftingIMSubCart";

// analytics
import { APPLY_COUPON_TEXTS } from "@cart-client/constants/Coupon";

// constants
import { CART_V2_APPLY_COUPON_ICON } from "@cart-client/constants/index";

export interface UseCouponlessSavingsReturnType {
    getIMCouponLessSavingsItem: (icon: string) => SavingsLineItemData | null;
}

/**
 * Hook to compute couponless savings for IM items, used in gifting cart flows
 * @param onSavingsLineItemLoad - Callback to handle savings line item load
 * @returns - Returns the function to get the IM couponless savings item
 */
export const useIMCouponlessSavingsData = (
    onSavingsLineItemLoad: (title?: string, subtitle?: string) => void,
): UseCouponlessSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const couponlessSavingWidgetData = useMemo(() => getIMCouponlessSavingWidgetData(cartResponse), [cartResponse]);

    const getIMCouponLessSavingsItem = useCallback(
        (icon: string): SavingsLineItemData | null => {
            if (isEmpty(couponlessSavingWidgetData)) {
                return null;
            }
            const title = {
                value: format(
                    APPLY_COUPON_TEXTS.X_SAVED_ON,
                    couponlessSavingWidgetData.savings,
                    couponlessSavingWidgetData.benefit_callout,
                ),
            };

            let subtitle: SavingsLineItemData["subtitle"];

            const rightSection = {
                type: SavingsRightSectionType.LABEL,
                value: APPLY_COUPON_TEXTS.APPLIED,
            };

            return {
                icon: icon || CART_V2_APPLY_COUPON_ICON,
                title,
                subtitle,
                rightSection,
                onLoad: () => {
                    onSavingsLineItemLoad(title.value);
                },
            };
        },
        [couponlessSavingWidgetData, onSavingsLineItemLoad],
    );

    return { getIMCouponLessSavingsItem };
};
